import { authFormShell } from './forms';

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const postAndReturnToken = async (pathSegment, data) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}${pathSegment}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.errors ? responseData.errors[0].msg : 'Network response was not ok');
    }

    if ('token' in responseData) {
      return responseData.token;
    } else {
      return null;
    }
  } catch (error) {
    console.error('There was a problem with the request:', error);
    return null;
  }
};

const authenticate = async (isLogin, authForm, setAuthForm, setError) => {
  const missingFields = [];
  if (!authForm.username) missingFields.push('username');
  if (!authForm.password) missingFields.push('password');
  if (!isLogin && !authForm.email) missingFields.push('email');

  if (missingFields.length > 0) {
    setError(`The form can't be submitted without the following data: ${missingFields.join(', ')}`);
    return { token: null, success: false };
  }

  setError('');

  try {
    let token;

    if (!isLogin) {
      token = await postAndReturnToken('auth/register', authForm);
    } else {
      token = await postAndReturnToken('auth/login', authForm);
    }

    if (token) {
      setAuthForm(authFormShell);
      console.log('Login or registration succeeded');
      return { token, success: true };
    } else {
      console.log('Login or registration failed');
      setError(isLogin ? 'There was a problem with logging in.' : 'There was a problem with registration.');
      return { token: null, success: false };
    }

  } catch (err) {
    console.error(err);
    setError(isLogin ? 'There was a problem with logging in.' : 'There was a problem with registration.');
    return { token: null, success: false };
  }
};

export { authenticate };

export const fetchData = async (pathSegment) => {
  const response = await fetch(`${import.meta.env.VITE_API_URL}${pathSegment}`);
  if (!response.ok) {
    throw new Error('Failed to fetch data');
  }
  return response.json();
};

export const joinDataArrays = (leftData, leftKey, rightData, rightKey, newProperties, newKey) => {
  const rightMap = rightData.reduce((map, item) => {
    const key = item[rightKey];
    map[key] = map[key] || [];
    map[key].push(item);
    return map;
  }, {});

  const joinedData = leftData.map(item => {
    const rightItems = rightMap[item[leftKey]] || [];
    const newItem = { ...item };
    if (newKey) {
      newItem[newKey] = rightItems.map(rightItem =>
        Object.fromEntries(
          Object.entries(newProperties).map(([newPropKey, rightPropKey]) => [
            newPropKey,
            rightItem[rightPropKey]
          ])
        )
      );
    } else {
      Object.entries(newProperties).forEach(([newPropKey, rightPropKey]) => {
        newItem[newPropKey] = rightItems.map(rightItem => rightItem[rightPropKey])[0];
      });
    }
    return newItem;
  });

  return joinedData;
};

export const reverseOrder = (items = []) => {
  return items.slice().reverse();
}

export const isFile = (image) => image instanceof File;

export const postAndReturnId = async (pathSegment, data) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}${pathSegment}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const responseData = await response.json();

    if ('id' in responseData) {
      const newId = responseData.id.id;
      return newId;
    } else {
      return null;
    }
  } catch (error) {
    console.error('There was a problem with the request:', error);
    return null;
  }
}

export const patchData = async (pathSegment, data) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}${pathSegment}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return true;
  } catch (error) {
    console.error('There was a problem with the request:', error);
    return false;
  }
}

export const deleteData = async (pathSegment, id) => {
  try {
    const url = `${import.meta.env.VITE_API_URL}${pathSegment}/${id}`;
    const response = await fetch(url, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete ${pathSegment}/${id}: ${response.statusText} (status: ${response.status})`);
    }

    console.log(`Deleted ${pathSegment}/${id} successfully.`);
    return true;
  } catch (error) {
    console.error('There was a problem with the DELETE request:', error);
    return false;
  }
};

export const createNewStatus = async (itemStatusForm, error, setError) => {
  try {
    let currentLocationId, currentSalesChannelId, itemStatusId, isItemUpdated;

    // Reset error state
    setError('');

    // Validate data and set error message if necessary
    const missingFields = [];
    const itemStatus = itemStatusForm.statuses[itemStatusForm.statuses.length - 1];

    if (itemStatus.status.name === 'sold') {
      if (!itemStatus.sales_channel.channel || !itemStatusForm.shipping_cost || !itemStatusForm.sale_price) {
        if (!itemStatus.sales_channel.channel) missingFields.push('sales channel');
        if (!itemStatusForm.shipping_cost) missingFields.push('shipping cost');
        if (!itemStatusForm.sale_price) missingFields.push('sale price');
        setError(`The new status can't be created without the following data: ${missingFields.join(', ')}`);
        return; // Exit early if validation fails
      }
    } else if (itemStatus.status.name === 'listed') {
      if (!itemStatus.sales_channel.channel || !itemStatusForm.location.name) {
        if (!itemStatus.sales_channel.channel) missingFields.push('sales channel');
        if (!itemStatusForm.location.name) missingFields.push('location');
        setError(`The new status can't be created without the following data: ${missingFields.join(', ')}`);
        return; // Exit early if validation fails
      }
    } else if (itemStatus.status.name === 'shelved') {
      if (!itemStatusForm.location.name) {
        missingFields.push('location');
        setError(`The new status can't be created without the following data: ${missingFields.join(', ')}`);
        return; // Exit early if validation fails
      }
    }

    // Fetch or create location ID
    currentLocationId = itemStatusForm.location.id || await postAndReturnId('location', { name: itemStatusForm.location.name });
    console.log('currentLocationId', currentLocationId);

    if (!currentLocationId) {
      setError('Failed to fetch or create location ID.');
      return;
    }

    const [locationId] = await Promise.all([currentLocationId]);
    console.log('locationId', locationId);

    // Update item data
    const updatedItem = {
      location_id: locationId,
      shipping_cost: itemStatusForm.shipping_cost,
      sale_price: itemStatusForm.sale_price
    };

    // Patch item data
    isItemUpdated = await patchData(`item/${itemStatusForm.id}`, updatedItem);
    console.log('isItemUpdated', isItemUpdated);

    if (!isItemUpdated) {
      setError('Failed to update item.');
      return;
    }

    // Fetch or create sales channel ID
    if (itemStatus.sales_channel.channel) {
      currentSalesChannelId = itemStatus.sales_channel.id || await postAndReturnId('sales-channel', { channel: itemStatus.sales_channel.channel });
      console.log('currentSalesChannelId', currentSalesChannelId);
      
      if (!currentSalesChannelId) {
        setError('Failed to fetch or create sales channel ID.');
        return;
      }
    }

    const [salesChannelId] = await Promise.all([currentSalesChannelId]);
    console.log('salesChannelId', salesChannelId);

    // Create new item status
    itemStatusId = await postAndReturnId('item-status', {
      entry_date: itemStatus.entry_date,
      item_id: itemStatusForm.id,
      status_id: itemStatus.status.id,
      sales_channel_id: salesChannelId,
      review_date: itemStatus.reviewDate
    });
    console.log('itemStatusId', itemStatusId);

    if (!itemStatusId) {
      setError('Failed to create new item status.');
      return;
    }

  } catch (err) {
    console.error('Error in createNewStatus:', err);
    setError('There was a problem creating the new status.');
  }
};

export const uploadImages = async (files) => {
  const formData = new FormData();

  for (const file of files.entries()) {
    formData.append('images', file[1]);
  }

  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}image`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (response.ok) {
      return data.fileNames;
    } else {
      throw new Error(data.error || 'Failed to upload images');
    }
  } catch (error) {
    console.error('Error uploading images:', error.message);
    throw error;
  }
};

export const deleteImage = async (fileName) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL}image/${encodeURIComponent(fileName)}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    if (response.ok) {
      return data.message;
    } else {
      throw new Error(data.error || 'Failed to delete image');
    }
  } catch (error) {
    console.error('Error deleting image:', error.message);
    throw error;
  }
};

export const getNewObjects = (startingArray, endingArray) => {
  const startingIds = new Set(startingArray.map(obj => obj.id));
  return endingArray.filter(obj => !startingIds.has(obj.id));
}

export const getMissingObjects = (startingArray, endingArray) => {
  const endingIds = new Set(endingArray.map(obj => obj.id));
  return startingArray.filter(obj => !endingIds.has(obj.id));
}

export const deleteMissingObjects = (missingArray) => {
  const deletePromises = missingArray.map(tag => deleteData('item-tag', tag.id));
  return Promise.all(deletePromises)
    .then(results => {
      console.log('All delete requests completed:', results);
    })
    .catch(error => {
      console.error('Error deleting some items:', error);
    });
}

export const formatDate = (date) => {
  return `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
};

export default {
  capitalizeFirstLetter,
  isFile,
  createNewStatus,
  postAndReturnId,
  deleteData,
  uploadImages,
  deleteImage,
  getNewObjects,
  getMissingObjects,
  deleteMissingObjects,
  formatDate,
}